<template>
    <!-- <Breadcrumbs main="Setting" mains="Patient Management" title="Patient Management" /> -->
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Report Sales</h5>
                    <div class="d-flex align-items-center gap-2">
                        <div v-if="role == 'FINANCE'" style="width: 200px">
                            <select v-model="(params as any).clinic" class="form-select">
                                <option :value="null" disabled>Select Clinic</option>
                                <option v-for="option in session.clinicOptions()" :key="option.ID" :value="option.name">
                                    {{ (option as any).description }}
                                </option>
                            </select>
                        </div>
                        <button :class="role != 'FINANCE' ? 'btn btn-sm btn-primary col-12': 'btn btn-sm btn-primary col-3'" @click="processDownload" :disabled="loading"><i class="fa fa-solid fa-plus"></i>XLS</button>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <DataTable :headers="headers" :items="patients" :loading="loading" :total="total" :params="params"
                    @update:params="updateParams">
                    <template #item.patient_photo="{ item }">
                        <div class="d-flex align-items-center">
                            <div class="avatar">
                                <img v-if="item.registrations.length > 0" :src="item.registrations[0].patient_photo ? item.registrations[0].patient_photo : item.registrations[0].registration_photo" class="rounded-circle" width="40" height="40" :alt="item.name">
                                <div v-else class="rounded-circle bg-light d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                                    <i class="fa fa-user text-secondary"></i>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #item.reservation="{ item }">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div v-for="(reservation, index) in item.reservations" :key="index">
                                    <p class="mb-0">{{ reservation.code }} (<b>{{ reservation.status }}</b>)</p>
                                    <p class="mb-0">{{ formatDateTime(reservation.reservation_date) }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #item.CreatedAt="{ item }">
                        <span class="text-primary">
                            {{ formatDateTime(item.CreatedAt) }}
                        </span>
                        <br>
                        <span class="text-secondary" v-if="item.reservations.length > 0">
                            ({{ formatDateTime(item.reservations[0].reservation_date) }})
                        </span>
                        <span class="text-secondary" v-else>
                            ({{ formatDateTime(item.UpdatedAt) }})
                        </span>
                    </template>
                    <template #item.registration="{ item }">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div v-for="(registration, index) in item.registrations" :key="index">
                                    <p class="mb-0">{{ registration.code }} (<b>{{ registration.status }}</b>)</p>
                                    <p class="mb-0">{{ formatDateTime(registration.registration_date) }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/DatatableDateRange.vue'
import { useSessionStore } from '@/store/session';
const session = useSessionStore()

const headers = [
    { text: 'Photo', value: 'patient_photo' },
    { text: 'Tanggal', value: 'CreatedAt' },
    { text: 'Info', value: 'information' },
    { text: 'Name', value: 'name' },
    { text: 'Telp', value: 'no_telp' },
    { text: 'Address', value: 'address' },
    { text: 'Sales', value: 'sales_name' },
    { text: 'Reservation', value: 'reservation' },
    { text: 'Registration', value: 'registration' },
]

// {{ Apply types to refs and reactive objects }}
const clinics = session.getUser().clinic
const email = session.getUser().email
const role = ref(session.getUser().role)
const patients = ref<any[]>([])
const loading = ref(false)
const total = ref(0)
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: '',
    clinic: clinics,
    email: role.value === 'SALES' ? email : ''
})

// Add this data data array
import * as clinic from "@/api/services/ClinicRequest";

const fetchPatient = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const resp = await clinic.postRequest("/patient/report", { ...params });
        patients.value = resp.data
        total.value = resp.total

    } catch (error) {
        toast.error('Failed to fetch patients')
    }
    loading.value = false
}

// {{ Add type annotation for newParams }}
const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchPatient()
}

onMounted(() => {
    role.value = session.getUser().role
})

const processDownload = async () => {
    loading.value = true
    try {
        // Simulate API delay
        const prms = { ...params }
        prms.size = 100000
        try {
            const response = await clinic.getDownload("/patient/xlsx", prms);
            // Create blob from response data
            const blob = new Blob([response], { type: 'application/vnd.ms-excel' });
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'patient-report.xlsx');
            // Trigger download
            document.body.appendChild(link);
            link.click();
            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download file. Please try again.');
        }

    } catch (error) {
        toast.error('Failed to fetch salesOrders')
    }
    loading.value = false
}

function formatDateTime(dateString: string, part: string = "-", partTime: string = " "): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}${part}${mm}${part}${yyyy}${partTime}${hh}:${min}`;
}
</script>